import { publicAxios } from "../api/axios";
import {
	getRGAFromLatLon,
	getSismicFromLatLon,
} from "../api/queries/georisques.queries";
import { ConstructionSiteDetailsFormData } from "../component/brokers/constructionSites/useConstructionSiteDetailsForm";

export type GeorisquesAddressPayload = {
	streetNumber?: string;
	streetName?: string;
	postalCode?: string;
	city?: string;
};

export type AddressParts = {
	streetNumber?: string;
	streetName?: string;
	postalCode?: string;
	city?: string;
};

export type AddressGeorisquesResult = {
	latlon: string;
	georisquesAddress: GeorisquesAddressPayload;
	rga?: number;
	sismic?: number;
};

function buildAddressString(parts: AddressParts): string {
	const { streetNumber, streetName, postalCode, city } = parts;
	const arr: string[] = [];
	if (streetNumber) arr.push(streetNumber);
	if (streetName) arr.push(streetName);
	if (postalCode) arr.push(postalCode);
	if (city) arr.push(city);
	return arr.join(" ").trim();
}

export async function fetchGeorisquesFromLatLon(
	latlon: string,
): Promise<{ rga?: number; sismic?: number }> {
	try {
		const [rgaData, sismicData] = await Promise.all([
			getRGAFromLatLon(latlon),
			getSismicFromLatLon(latlon),
		]);
		const rga = !Number.isNaN(Number(rgaData?.codeExposition))
			? Number(rgaData?.codeExposition)
			: undefined;
		const sismic = !Number.isNaN(Number(sismicData?.code_zone - 1))
			? Number(sismicData?.code_zone - 1)
			: undefined;
		return { rga, sismic };
	} catch (error) {
		console.error("Erreur lors du calcul des géorisques:", error);
		return {};
	}
}

export async function searchAddressAndFetchGeorisques(
	address: AddressParts,
): Promise<AddressGeorisquesResult | null> {
	const { postalCode, city } = address;
	if (!postalCode || !city) return null;

	const addressToSearch = buildAddressString(address);
	if (!addressToSearch) return null;

	try {
		const url = `/address/search/${encodeURIComponent(addressToSearch)}`;
		const { data: addresses } = await publicAxios.get(url, {
			params: { limit: 1 },
		});

		if (!addresses?.length) return null;

		const first = addresses[0];
		const latlon = first.geometry.coordinates.join(",");
		const geo = first.properties;
		const georisquesAddress: GeorisquesAddressPayload = {
			streetNumber: geo.housenumber ?? undefined,
			streetName: geo.street ?? undefined,
			postalCode: geo.postcode ?? undefined,
			city: geo.city ?? undefined,
		};

		const { rga, sismic } = await fetchGeorisquesFromLatLon(latlon);
		return { latlon, georisquesAddress, rga, sismic };
	} catch (error: unknown) {
		const message =
			error &&
			typeof error === "object" &&
			"response" in error &&
			error.response &&
			typeof error.response === "object" &&
			"data" in error.response &&
			error.response.data &&
			typeof error.response.data === "object" &&
			"message" in error.response.data &&
			typeof (error.response.data as { message: string }).message === "string"
				? (error.response.data as { message: string }).message
				: error instanceof Error
					? error.message
					: "Aucune adresse trouvée.";
		throw new Error(message);
	}
}

export const reprocessAddressAndGeorisques = async (
	formData: ConstructionSiteDetailsFormData,
): Promise<Partial<ConstructionSiteDetailsFormData>> => {
	const result = await searchAddressAndFetchGeorisques({
		streetNumber: formData.streetNumber,
		streetName: formData.streetName,
		postalCode: formData.postalCode,
		city: formData.city,
	});
	if (result) {
		return {
			latlon: result.latlon,
			georisquesAddress: result.georisquesAddress,
			rga: result.rga,
			sismic: result.sismic,
		};
	}
	const fallback: Partial<ConstructionSiteDetailsFormData> = {};
	if (formData.latlon != null) fallback.latlon = formData.latlon;
	if (formData.georisquesAddress != null)
		fallback.georisquesAddress = formData.georisquesAddress;
	if (formData.rga != null) fallback.rga = formData.rga;
	if (formData.sismic != null) fallback.sismic = formData.sismic;
	return fallback;
};
